import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const COMMON_USAGE_ROWS = [
  {
    module: 'eraf-data-jpa (C027)',
    api: 'RetentionExecutor.purge(def)',
    role: '물리 삭제 실행 위임',
    snippet: 'retentionExecutor.purge(adapter.toDef(policy))',
  },
  {
    module: 'eraf-data-jpa (C027)',
    api: 'RetentionSqlBuilder.countSql/totalCountSql',
    role: 'dry-run COUNT (비파괴 미리보기)',
    snippet: 'jdbc.queryForObject(sqlBuilder.countSql(def), Long.class)',
  },
  {
    module: 'eraf-data-jpa (C027)',
    api: 'RetentionGuard.validatePolicies',
    role: '저장 전 6가드 검증',
    snippet: 'retentionGuard.validatePolicies(propsView)',
  },
  {
    module: 'eraf-data-jpa (C027)',
    api: 'RetentionResult / RetentionRunStatus',
    role: 'purge 결과 → PurgeResponse 매핑',
    snippet: 'PurgeResponse.from(result)',
  },
  {
    module: 'eraf-data-jpa (C027)',
    api: 'RetentionProperties.allowedTables/maxDeleteRatio',
    role: 'allowlist 드롭다운·ratio 가드 표시',
    snippet: 'props.getAllowedTables()',
  },
  {
    module: 'eraf-core (C014)',
    api: '@Audited(action="RETENTION_PURGE")',
    role: 'purge/CRUD 자기 감사',
    snippet: '@Audited(action="RETENTION_PURGE", resource="retention-policy")',
  },
  {
    module: 'eraf-core (C002)',
    api: 'ApiResponse.page(...)',
    role: '목록·이력 표준 응답',
    snippet: 'ApiResponse.page(service.search(cond, p))',
  },
  {
    module: 'eraf-core (C001)',
    api: 'BusinessException(RETENT-409-002)',
    role: 'dry-run 미선행 차단',
    snippet: 'throw new BusinessException(DRY_RUN_REQUIRED)',
  },
  {
    module: 'eraf-security (C030)',
    api: '@HasPermission("RETENTION_PURGE")',
    role: 'purge 메서드 권한 (이중 방어)',
    snippet: '@HasPermission("RETENTION_PURGE")',
  },
  {
    module: 'eraf-data-jpa (C041/C042)',
    api: 'SpecificationBuilder + SortWhitelist',
    role: '동적 검색·정렬 + 인젝션 차단',
    snippet: 'SpecificationBuilder.of(...).eq("targetTable", t)...',
  },
];

/**
 * RetentionCommonUsagePanel — 공통 모듈 시연 패널 (biz-foundation §4.11 의무)
 */
export function RetentionCommonUsagePanel() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      style={{
        marginTop: 'var(--spacing-6)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-family-sans)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          fontWeight: 600,
          textAlign: 'left',
        }}
      >
        <span>&#9660; {t('retentionAdmin.commonUsage.title')}</span>
        <span style={{ fontSize: 'var(--font-size-xs)' }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
            }}
            aria-label={t('retentionAdmin.commonUsage.title')}
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {[
                  'retentionAdmin.commonUsage.col.module',
                  'retentionAdmin.commonUsage.col.api',
                  'retentionAdmin.commonUsage.col.role',
                  'retentionAdmin.commonUsage.col.snippet',
                ].map((key) => (
                  <th
                    key={key}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMMON_USAGE_ROWS.map((row, i) => (
                <tr key={i}>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.module}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-primary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.api}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    {row.role}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-muted)',
                      fontSize: 'var(--font-size-xs)',
                    }}
                  >
                    {row.snippet}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
